import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { GetDiscoveryDocument, SetSettingDocument } from "#app/lib/data";

/**
 * The dismissal sentinel written to a `discovery_<name>` setting key. The
 * setSetting value is typed as a JSON object, but discovery dismissal has
 * always stored the bare literal `1` — the cast keeps that wire format.
 */
export const DISCOVERY_DISMISSED = 1 as unknown as Record<string, never>;

/** The user-scoped setting key a discovery flag's dismissal is stored under. */
export const discoverySettingKey = (name: string) => `discovery_${name}`;

/**
 * Drives a `<DiscoveryIndicator>`. Visibility resolves on the server
 * (`discovery` query): hidden once the user dismisses the flag (a `1` at
 * the user-scoped setting key `discovery_{name}`), and never shown to
 * members created after the feature's release date — the global
 * `discovery_{name}` setting row stamped by the feature's migration, or
 * the optional `releasedAt` fallback for flags dated client-side (e.g.
 * version notes, dated by the release service).
 *
 * REQUIRED: every new `useDiscovery("<name>")` call site ships with a core
 * migration inserting that global row (`discovery_<name>`, workspace NULL /
 * user NULL, value `{ releasedAt: now() }`, ON CONFLICT DO NOTHING — see
 * step 14 of `1779820563000-Permissions.ts`), unless a `releasedAt` is
 * passed here. With no date at all the server can't age-gate and the dot
 * shows to every brand-new member forever. Full rules:
 * `packages/announcements/README.md` "Discovery indicators".
 *
 * Returns `visible: false` until the server confirms — a brand-new member
 * must never see a flash of indicators while the query is in flight. Call
 * `dismiss()` to write the dismissal and immediately hide the indicator.
 *
 * Scope is user-only (no workspace) — dismissal follows the member, not
 * the workspace.
 */
export function useDiscovery(
  name: string | null,
  releasedAt?: string | null,
): {
  visible: boolean;
  dismiss: () => void;
} {
  const key = discoverySettingKey(name ?? "");
  const variables = useMemo(
    () => ({ name: name ?? "", releasedAt: releasedAt ?? null }),
    [name, releasedAt],
  );

  const { data } = useQuery(GetDiscoveryDocument, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: name === null,
  });

  const [setSetting] = useMutation(SetSettingDocument);

  const visible = data?.discovery === true;

  const dismiss = useCallback(() => {
    if (!visible) return;
    setSetting({
      variables: { key, value: DISCOVERY_DISMISSED },
      optimisticResponse: { setSetting: true },
      update: (cache) => {
        cache.writeQuery({
          query: GetDiscoveryDocument,
          variables,
          data: { discovery: false },
        });
      },
    });
  }, [visible, key, variables, setSetting]);

  return { visible, dismiss };
}
